import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import {
  ACTIVE_ERROR_TYPE,
  DashboardInstallationState,
  DashboardSelectedTab,
  DashboardStore,
  DashboardStoreStatusType,
  DashboardTileOrder,
  DashboardTileTabValues,
  DashboardTileType,
  dashboardTileTypeToDashboardTileObjectMapper,
  InstallationWithAnomaliesTabsType,
  InstallationWithFaultTabsType,
  LastConnectedSystemTabsType,
} from '@store';
import {
  DashboardDevice,
  DashboardInstallationData,
  DashboardInstallationsTileTranslations,
  DashboardSmartNote,
  FaultMessageSmartnotes,
  SubscriptionPlan,
} from '@vi-iot/client';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private activeDashboardTabs$: BehaviorSubject<DashboardSelectedTab[]> = new BehaviorSubject<
    DashboardSelectedTab[]
  >([]);

  constructor(private dashboardStore: DashboardStore, private translateService: TranslateService) {}

  updateTileOrdersAfterDrop(
    previousTile: DashboardTileOrder,
    currentTile: DashboardTileOrder,
    tilesOrder: DashboardTileOrder[]
  ): void {
    const prevTileIndex = tilesOrder.indexOf(previousTile);
    const targetTileIndex = tilesOrder.indexOf(currentTile);

    if (prevTileIndex === targetTileIndex) {
      return;
    }

    moveItemInArray(tilesOrder, prevTileIndex, targetTileIndex);
    this.dashboardStore.saveTilesOrder(tilesOrder, false);
  }

  getDashboardInstallationImagePath(installation: DashboardInstallationData): string {
    const defaultImagePath = 'assets/img/devices/device_small.png';
    const vin = installation?.device?.vin;
    if (!vin) {
      return defaultImagePath;
    }

    const matchingDevice = installation.devices?.find(
      (device: DashboardDevice) => device?.vin === vin
    );

    return matchingDevice?.pictogramImage || matchingDevice?.materialImage || defaultImagePath;
  }

  getInstallationCountFromDashboardInstallationState$(
    dashboardInstallationState$: Observable<DashboardInstallationState>
  ): Observable<number> {
    return dashboardInstallationState$?.pipe(
      map(
        (dashboardInstallationData: DashboardInstallationState) =>
          dashboardInstallationData.installationsCount
      )
    );
  }

  setActiveTabForTile(tileType: DashboardTileType, activeTab: string): void {
    const currentData: DashboardSelectedTab[] = this.activeDashboardTabs$.value;
    let updatedData: DashboardSelectedTab[] = [
      ...currentData,
      { tileType, selectedTabValue: activeTab },
    ];

    if (currentData.some((tab: DashboardSelectedTab) => tab.tileType === tileType)) {
      updatedData = currentData.map((tab: DashboardSelectedTab) =>
        tab.tileType === tileType ? { ...tab, selectedTabValue: activeTab } : tab
      );
    }

    this.activeDashboardTabs$.next(updatedData);
  }

  getActiveTabForTile(tileType: DashboardTileType): Observable<DashboardSelectedTab> {
    return this.activeDashboardTabs$.pipe(
      map(
        (activeTabs: DashboardSelectedTab[]) =>
          activeTabs.find((tab) => tab.tileType === tileType) ?? {
            tileType: tileType,
            selectedTabValue: dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs[0].value,
          }
      )
    );
  }

  dashboardTileHasMapper(tileType: DashboardTileType): boolean {
    return !!dashboardTileTypeToDashboardTileObjectMapper[tileType];
  }

  dashboardTileHasTabs(tileType: DashboardTileType): boolean {
    return !!dashboardTileTypeToDashboardTileObjectMapper[tileType]?.tabs;
  }

  // TODO: Move translations to CustomTranslationsService after merging VIG-13699
  dashboardTileTypeTranslations(key: string): DashboardInstallationsTileTranslations {
    switch (key) {
      case DashboardTileType.InstallationsWithoutHEMS:
        return {
          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITHOUT_HEMS.BANNERS.EMPTY_DATA'
          ),
        };

      case DashboardTileType.UpcomingMaintenanceForInstallations:
        return {
          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.UPCOMING_MAINTENANCE_FOR_INSTALLATIONS.BANNERS.EMPTY_DATA'
          ),
        };

      case DashboardTileType.InstallationsWithFault:
        return {
          tabs: {
            [InstallationWithFaultTabsType.All as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_FAULT.FILTERS.ALL'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_FAULT.BANNERS.EMPTY_DATA'
              ),
            },
            [InstallationWithFaultTabsType.Error as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_FAULT.FILTERS.ERROR'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_FAULT.BANNERS.EMPTY_ERROR_DATA'
              ),
            },
            [InstallationWithFaultTabsType.Locked as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_FAULT.FILTERS.LOCKED'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_FAULT.BANNERS.EMPTY_LOCKED_DATA'
              ),
            },
          },
          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_FAULT.BANNERS.EMPTY_DATA'
          ),
        };

      case DashboardTileType.LastConnectedSystems:
        return {
          tabs: {
            [LastConnectedSystemTabsType.Yesterday as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.FILTERS.YESTERDAY'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.BANNERS.EMPTY_ERROR_DATA_FOR_SELECTED_TAB'
              ),
            },
            [LastConnectedSystemTabsType.Today as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.FILTERS.TODAY'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.BANNERS.EMPTY_ERROR_DATA_FOR_SELECTED_TAB'
              ),
            },
            [LastConnectedSystemTabsType.Last7Days as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.FILTERS.LAST_SEVEN_DAYS'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.BANNERS.EMPTY_ERROR_DATA_FOR_SELECTED_TAB'
              ),
            },
            [LastConnectedSystemTabsType.Last30Days as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.FILTERS.THIS_MONTH'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.BANNERS.EMPTY_ERROR_DATA_FOR_SELECTED_TAB'
              ),
            },
            [LastConnectedSystemTabsType.LastYear as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.FILTERS.THIS_YEAR'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.BANNERS.EMPTY_ERROR_DATA_FOR_SELECTED_TAB'
              ),
            },
          },

          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.LAST_CONNECTED_SYSTEMS.BANNERS.EMPTY_DATA'
          ),
        };

      case DashboardTileType.InstallationsWithAnomalies:
        return {
          tabs: {
            [InstallationWithAnomaliesTabsType.All as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.FILTERS.ALL'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.BANNERS.EMPTY_DATA_FOR_SELECTED_TAB'
              ),
            },
            [InstallationWithAnomaliesTabsType.Warning as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.FILTERS.WARNING'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.BANNERS.EMPTY_DATA_FOR_SELECTED_TAB'
              ),
            },
            [InstallationWithAnomaliesTabsType.Information as DashboardTileTabValues]: {
              tabName: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.FILTERS.INFORMATION'
              ),
              tabEmptyData: this.translateService.instant(
                '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.BANNERS.EMPTY_DATA_FOR_SELECTED_TAB'
              ),
            },
          },

          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.BANNERS.EMPTY_DATA'
          ),
        };

      case DashboardTileType.InstallationsWithoutSRC:
        return {
          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITHOUT_SRC.BANNERS.EMPTY_DATA'
          ),
        };

      case DashboardTileType.ContractsEndingSoon:
        return {
          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.CONTRACTS_ENDING_SOON.BANNERS.EMPTY_DATA'
          ),
        };

      case DashboardTileType.DevicesWithLowWifi:
        return {
          tileEmptyData: this.translateService.instant(
            '+DASHBOARD.DASHBOARD_TILES.DEVICES_WITH_LOW_WIFI.BANNERS.EMPTY_DATA'
          ),
        };
    }
  }

  getInstallationPackage(installation: DashboardInstallationData): SubscriptionPlan | null {
    if (installation.device.plans.includes(SubscriptionPlan.Pro)) {
      return SubscriptionPlan.Pro;
    }
    if (installation.device.plans.includes(SubscriptionPlan.Plus)) {
      return SubscriptionPlan.Plus;
    }

    return null;
  }

  isDashboardTabDataEmpty(tileType: DashboardTileType): Observable<boolean> {
    if (this.dashboardTileHasTabs(tileType)) {
      return this.getActiveTabForTile(tileType).pipe(
        take(1),
        concatLatestFrom(({ selectedTabValue }) =>
          this.dashboardStore.getStoreStatus(tileType, selectedTabValue)
        ),
        map(([_selectedTab, dashboardStoreStatus]) => {
          return dashboardStoreStatus === DashboardStoreStatusType.EmptyTab;
        })
      );
    }

    return of(false);
  }

  getLatestSmartNotes(devices: DashboardDevice[]): FaultMessageSmartnotes {
    const noSmartNotes: FaultMessageSmartnotes = {
      smartNotes: [],
      needTooltip: false,
    };
    if (!devices) {
      return noSmartNotes;
    }
    const allSmartNotes: DashboardSmartNote[] = devices.reduce(
      (acc: DashboardSmartNote[], item: DashboardDevice) => {
        if (item.smartNotes && Array.isArray(item.smartNotes)) {
          acc.push(...item.smartNotes);
        }

        return acc;
      },
      []
    );

    const deviceErrorSmartNotes = allSmartNotes.filter(
      (smartNote) => smartNote.type === ACTIVE_ERROR_TYPE.DEVICE_ERROR
    );

    if (!deviceErrorSmartNotes) {
      return noSmartNotes;
    }

    const sortedSmartNotes = deviceErrorSmartNotes.sort((a, b) => {
      const dateA = new Date(a.content.appearedAt).getTime();
      const dateB = new Date(b.content.appearedAt).getTime();

      return dateB - dateA;
    });

    const latestSmartNotes = sortedSmartNotes.slice(0, 3);

    return {
      smartNotes: latestSmartNotes,
      needTooltip: deviceErrorSmartNotes.length > 3,
    };
  }
}
